import {ReactNode, useMemo} from "react";
import {useTranslation} from "react-i18next";
import {css, Theme, useTheme} from "@emotion/react";
import {Col, Container, Row} from "@pg-design/grid";
import {
    flex,
    flexAlignCenter,
    flexDirection,
    flexJustifyCenter,
    mb,
    ml,
    mv,
    onDesktop,
    pv
} from "@pg-design/helpers-css";
import {BrandChartIcon, BrandCheckboxCheckedIcon, BrandClockIcon, BrandMapMarkersIcon} from "@pg-design/icons";
import {Text} from "@pg-design/text";

type Pillar = {
    title: string;
    description: string;
    icon: ReactNode;
};

interface IReliableDataPillarProps {
    title: string;
    description: string;
    icon: ReactNode;
}

const ReliableDataPillar = ({title, description, icon}: IReliableDataPillarProps) => (
    <div css={[pillar, flexAlignCenter]}>
        {icon}
        <Text strong variant="body_copy_1" align="center" as="h3" css={[pillarTitle, mv(3)]}>
            {title}
        </Text>
        <Text align="center" variant="body_copy_1">
            {description}
        </Text>
    </div>
);

export const ReliableData = () => {
    const {t} = useTranslation();
    const theme = useTheme();

    const pillars = useMemo<Pillar[]>(() => {
        const iconProps = {size: "15" as const, wrapperColor: theme.colors.primary, wrapperSize: "15.5" as const};

        return [
            {
                title: t("landing.analyticalPlatformReliableData.pillar1.title"),
                description: t("landing.analyticalPlatformReliableData.pillar1.description"),
                icon: <BrandMapMarkersIcon {...iconProps} />
            },
            {
                title: t("landing.analyticalPlatformReliableData.pillar2.title"),
                description: t("landing.analyticalPlatformReliableData.pillar2.description"),
                icon: <BrandClockIcon {...iconProps} />
            },
            {
                title: t("landing.analyticalPlatformReliableData.pillar3.title"),
                description: t("landing.analyticalPlatformReliableData.pillar3.description"),
                icon: <BrandChartIcon {...iconProps} />
            }
        ];
    }, [t, theme.colors.primary]);

    const comingSoonLabel = t("landing.analyticalPlatformReliableData.comingSoon");

    const sources = useMemo(
        () => [
            {key: "item1", label: t("landing.analyticalPlatformReliableData.sources.item1")},
            {
                key: "item1sub1",
                label: t("landing.analyticalPlatformReliableData.sources.item1sub1"),
                comingSoon: true
            },
            {key: "item1sub2", label: t("landing.analyticalPlatformReliableData.sources.item1sub2")},
            {key: "item2", label: t("landing.analyticalPlatformReliableData.sources.item2")}
        ],
        [t]
    );

    return (
        <Container as="section" fluid css={[section(theme), flexAlignCenter]}>
            <Container>
                <Text variant="headline_1" align="center" as="h2" css={mb(6)}>
                    {t("landing.analyticalPlatformReliableData.heading")}
                </Text>

                <Row css={[featuresRow(theme), flexJustifyCenter]}>
                    {pillars.map((pillar) => (
                        <Col key={pillar.title} noGutters css={flexJustifyCenter} sm={4} md={4} lg={4}>
                            <Col noGutters md={10} lg={10}>
                                <ReliableDataPillar
                                    title={pillar.title}
                                    description={pillar.description}
                                    icon={pillar.icon}
                                />
                            </Col>
                        </Col>
                    ))}

                    <Col noGutters sm={4} md={12} lg={12} css={columnCenter}>
                        <div css={sourcesBlock}>
                            <Text variant="headline_6" align="left" as="h3" css={mv(3)}>
                                {t("landing.analyticalPlatformReliableData.sources.title")}
                            </Text>
                            <ul css={sourcesList}>
                                {sources.map((item) => (
                                    <li key={item.key} css={sourcesListItem}>
                                        <BrandCheckboxCheckedIcon
                                            size="3.2"
                                            wrapperColor="transparent"
                                            wrapperSize="3.2"
                                        />
                                        <Text variant="body_copy_1" css={ml(1.5)} as="span">
                                            {item.label}
                                            {item.comingSoon ? (
                                                <Text variant="body_copy_1" as="span" color={theme.colors.gray[600]}>
                                                    {" "}
                                                    ({comingSoonLabel})
                                                </Text>
                                            ) : null}
                                        </Text>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    </Col>
                </Row>
            </Container>
        </Container>
    );
};

const section = (theme: Theme) => css`
    ${pv(6)};
    flex-direction: column;
    background-color: ${theme.colors.gray[100]};

    ${onDesktop(css`
        ${pv(9)};
    `)}
`;

const featuresRow = (theme: Theme) => css`
    row-gap: 6.4rem;

    @media (min-width: ${theme.breakpoints.md}) {
        row-gap: 8rem;
    }
`;

const pillar = css`
    flex-direction: column;
`;

const pillarTitle = css`
    text-transform: uppercase;
`;

const sourcesBlock = css`
    ${flexDirection("column")};
    align-items: flex-start;
    width: fit-content;
    text-align: start;
`;

const sourcesList = css`
    margin: 0;
    padding: 0;
    list-style: none;
`;

const sourcesListItem = css`
    ${flexAlignCenter};
    ${mb(1)};
`;

const columnCenter = css`
    ${flexDirection("column")};
    ${flexAlignCenter};
`;
